import { ExpandMore, CheckCircle, RadioButtonUnchecked, Edit, Info } from '@mui/icons-material';
import {
    Box,
    Typography,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    LinearProgress,
    Chip,
    List,
    ListItem,
    ListItemText,
    IconButton,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { useState, useMemo, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AdditionalDetailsStep from '../components/additionalDetailsStep';
import BusinessOwnershipStep from '../components/businessOwnershipStep';
import EducationStep from '../components/educationStep';
import FamilyMembersStep from '../components/familyMembersStep';
import JobOfferStep from '../components/jobOfferStep';
import LanguageProficiencyStep from '../components/languageProficiencyStep';
import PersonalInfoStep from '../components/personalInfoStep';
import RelativesAbroadStep from '../components/relativesAbroadStep';
import SpouseInfoStep from '../components/spouseInfoStep';
import WorkExperienceStep from '../components/workExperienceStep';
import Loader from 'components/v2/common/loader';
import PageBackground from 'components/v2/PageBackground';

import api from 'api';
import PERMISSION from 'assets/permissions.json';
import {
    CompleteFormNameMapping,
    formStepFieldDescriptions,
    VisaApplicationStepName,
} from 'constant';
import useTranslate from 'hooks/useTranslate';
import {
    useAdditionalDetails,
    useCompletedForms,
    usePatchCompletedForm,
    usePurpose,
} from 'hooks/useVisaApplication';
import { setAlert } from 'redux/alert';
import { RootState } from 'redux/store';
import { setAssessmentStartTimestamps } from 'redux/user/user-slice';
import { startAssessmentOnce } from 'utils/assessmentStart';
import { stampCompletedAssessmentIfReady } from 'utils/completedAssessment';
import {
    parseHasSpouseFromPayload,
    parseSpouseUserIdFromPayload,
    persistHasSpouseDraft,
    persistSpouseLifecycle,
} from 'utils/hasSpouse';
import { getErrorMessage } from 'utils/v2/errorHelper';
import { getFormDraft, updateFormDraft } from 'utils/v2/storage';
import { getVisaApplicationPath } from 'utils/v2/visaApplication';

const stepLabelKey: Record<string, string> = {
    [VisaApplicationStepName['Personal Info']]: 'personalInfo',
    [VisaApplicationStepName['Additional Details']]: 'additionalDetails',
    [VisaApplicationStepName['Spouse/Common Law Partner']]: 'spouse/CommonLawPartner',
    [VisaApplicationStepName['Family Members']]: 'familyMembers',
    [VisaApplicationStepName['Language Proficiency']]: 'languageProficiency',
    [VisaApplicationStepName.Education]: 'education',
    [VisaApplicationStepName['Work Experience']]: 'workExperience',
    [VisaApplicationStepName['Job Offer']]: 'jobOffer',
    [VisaApplicationStepName['Relatives Abroad']]: 'relativesAbroad',
    [VisaApplicationStepName['Business Ownership']]: 'businessOwnership',
};

const VisaApplication = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const user = useSelector((state: RootState) => state.user.entity);
    const { translate } = useTranslate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const { permissionsList } = useSelector((state: RootState) => state.permissions);
    const { applications: candidateApps } = useSelector((state: RootState) => state.applications);
    // Only enable the query once Redux has a real user id (avoids key thrash 0 → id).
    const userId = user?.id && user.id > 0 ? user.id : 0;
    const { data: completedForms } = useCompletedForms(userId);
    const { mutateAsync: patchCompletedForm } = usePatchCompletedForm();
    const { data: purpose, isLoading: purposeLoading } = usePurpose();
    const { data: additionalDetails } = useAdditionalDetails();

    const [expanded, setExpanded] = useState<string>('');
    const [hasSpouse, setHasSpouse] = useState<string>('No');
    const [currentPurpose, setCurrentPurpose] = useState<string | null>(null);
    const [mobileHelpOpen, setMobileHelpOpen] = useState<Record<string, boolean>>({});
    /** Optimistic step completion keys (API form keys like personal_info). */
    const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
    const [formData, setFormData] = useState(() => {
        const draft = getFormDraft();
        return draft?.data || {};
    });

    useEffect(() => {
        if (!userId) return;

        void startAssessmentOnce(userId).then((timestamps) => {
            if (timestamps) {
                dispatch(setAssessmentStartTimestamps(timestamps));
            }
        });
    }, [dispatch, userId]);

    useEffect(() => {
        if (purpose?.purpose) {
            setCurrentPurpose(purpose.purpose);
        }
    }, [purpose]);

    useEffect(() => {
        const flag = parseHasSpouseFromPayload(additionalDetails);
        const spouseUserId = parseSpouseUserIdFromPayload(additionalDetails);
        if (flag === undefined) {
            return;
        }
        setHasSpouse(flag ? 'Yes' : 'No');
        persistSpouseLifecycle(flag, spouseUserId);
    }, [additionalDetails]);

    // Seed local completion marks from API so check icons stay in sync on load
    useEffect(() => {
        if (!completedForms) return;
        const keys = Object.entries(completedForms)
            .filter(([, done]) => Boolean(done))
            .map(([key]) => key);
        if (keys.length > 0) {
            setCompletedSteps((prev) => {
                const next = new Set(prev);
                keys.forEach((k) => next.add(k));
                return next;
            });
        }
    }, [completedForms]);

    const visaProcessSteps = useMemo(() => {
        if (!currentPurpose) return [];
        return getVisaApplicationPath(currentPurpose, hasSpouse, 'Yes');
    }, [currentPurpose, hasSpouse]);

    // Set expanded only on first load — never override after user interaction
    const hasInitialized = useRef(false);

    useEffect(() => {
        if (hasInitialized.current || !completedForms || visaProcessSteps.length === 0) return;

        const firstIncomplete = visaProcessSteps.find(
            (step) => !completedForms[CompleteFormNameMapping[step]]
        );
        setExpanded(firstIncomplete ?? '');
        hasInitialized.current = true;
    }, [completedForms, visaProcessSteps]);

    // Calculate overall progress using useMemo
    const { fieldProgress, completedCount } = useMemo(() => {
        const totalSteps = visaProcessSteps.length;
        if (totalSteps === 0) return { fieldProgress: 0, completedCount: 0 };

        const completedCount = visaProcessSteps.filter((step) => {
            const key = CompleteFormNameMapping[step];
            return completedSteps.has(key) || Boolean(completedForms?.[key]);
        }).length;

        const fieldProgress = (completedCount / totalSteps) * 100;
        return { fieldProgress, completedCount };
    }, [completedForms, completedSteps, visaProcessSteps]);

    const isApplicationComplete = Math.round(fieldProgress) >= 100;

    const handleAccordionChange =
        (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded ? panel : '');
        };

    /**
     * PATCH /candidate_info/:id/completed-forms for the current step
     * (same as FormBox.formCompletedStatusChange). Cache is updated in the
     * mutation onSuccess — do not refetch GET here (that caused duplicate calls).
     */
    const markStepCompletedOnServer = async (stepName: string) => {
        const formKey = CompleteFormNameMapping[stepName];
        if (!userId || !formKey) return;

        const payload: Record<string, boolean> = { [formKey]: true };
        // Personal Info step also saves purpose — mark both like multi-table FormBox posts
        if (stepName === VisaApplicationStepName['Personal Info']) {
            payload.purpose = true;
        }

        try {
            await patchCompletedForm({
                userId,
                payload,
            });
        } catch (error) {
            console.error('Failed to update completed-forms status', error);
            // Non-blocking: local UI still shows optimistic complete mark
        }
    };

    const stampAssessmentIfFormsComplete = async (stepName: string) => {
        const formKey = CompleteFormNameMapping[stepName];
        const patched: Record<string, boolean> = {};
        if (formKey) {
            patched[formKey] = true;
        }
        if (stepName === VisaApplicationStepName['Personal Info']) {
            patched.purpose = true;
        }
        const requiredKeys = visaProcessSteps
            .map((step) => CompleteFormNameMapping[step])
            .filter((key): key is string => Boolean(key));
        await stampCompletedAssessmentIfReady({
            userId,
            completedAssessment: user.completed_assessment,
            patched,
            requiredKeys,
        });
    };

    const handleNext = async (stepData: Record<string, unknown>) => {
        const currentStep = expanded;
        const formKey = CompleteFormNameMapping[currentStep];
        const updatedData = { ...formData, ...stepData };
        setFormData(updatedData);

        // Optimistic complete mark (API form key)
        if (formKey) {
            setCompletedSteps((prev) => new Set(prev).add(formKey));
        }

        // Save draft to localStorage
        if (user?.id) {
            updateFormDraft(currentStep, updatedData, user.id);
        }

        // Persist completed-forms + refresh latest status for checkmarks / progress
        await markStepCompletedOnServer(currentStep);
        await stampAssessmentIfFormsComplete(currentStep);

        // Expand next step
        const nextStep = visaProcessSteps[visaProcessSteps.indexOf(currentStep) + 1];
        setExpanded(nextStep || '');
    };

    const handleBack = () => {
        const currentIndex = visaProcessSteps.indexOf(expanded);
        if (currentIndex > 0) {
            setExpanded(visaProcessSteps[currentIndex - 1]);
        }
    };

    const handleRedirect = () => {
        if (permissionsList.some((permission) => permission?.name === PERMISSION.VIEW)) {
            navigate(`/visa-process/${candidateApps[0]?.id}/results`);
        } else {
            navigate(`/visa-process/${candidateApps[0]?.id}/agreement`);
        }
    };

    const submitAssessmentStatus = async (userId: number | undefined) => {
        try {
            const resp = await api.tableInput.saveAssessmentStatus(userId || 0);
            if (resp?.data.url) {
                window.location.assign(resp.data.url);
            } else {
                handleRedirect();
            }
        } catch (error) {
            dispatch(setAlert({ message: getErrorMessage(error), type: 'error' }));
        }
    };

    const handleSubmit = async (stepData: Record<string, unknown>) => {
        const currentStep = expanded;
        const formKey = CompleteFormNameMapping[currentStep];
        const finalData = { ...formData, ...stepData };
        setFormData(finalData);

        if (formKey) {
            setCompletedSteps((prev) => new Set(prev).add(formKey));
        }

        await markStepCompletedOnServer(currentStep);
        await stampAssessmentIfFormsComplete(currentStep);

        // Move to next step if available, otherwise finish assessment
        const currentIndex = visaProcessSteps.indexOf(currentStep);
        const nextStep = visaProcessSteps[currentIndex + 1];
        if (nextStep) {
            if (user?.id) {
                updateFormDraft(currentStep, finalData, user.id);
            }
            setExpanded(nextStep);
        } else {
            await submitAssessmentStatus(user?.id);
        }
    };

    const renderStep = (step: string) => {
        const commonProps = {
            formData,
            hasSpouse,
            isLastStep: visaProcessSteps.indexOf(step) === visaProcessSteps.length - 1,
            onNext: handleNext,
            onBack: handleBack,
        };

        switch (step) {
            case VisaApplicationStepName['Personal Info']:
                return <PersonalInfoStep {...commonProps} onPurposeChange={setCurrentPurpose} />;
            case VisaApplicationStepName['Additional Details']:
                return (
                    <AdditionalDetailsStep
                        {...commonProps}
                        handleTravelingWithPartner={(value) => {
                            setHasSpouse(value);
                            persistHasSpouseDraft(value === 'Yes');
                        }}
                    />
                );
            case VisaApplicationStepName['Spouse/Common Law Partner']:
                return <SpouseInfoStep {...commonProps} />;
            case VisaApplicationStepName['Family Members']:
                return <FamilyMembersStep {...commonProps} />;
            case VisaApplicationStepName['Language Proficiency']:
                return <LanguageProficiencyStep {...commonProps} />;
            case VisaApplicationStepName['Education']:
                return <EducationStep {...commonProps} />;
            case VisaApplicationStepName['Work Experience']:
                return <WorkExperienceStep {...commonProps} />;
            case VisaApplicationStepName['Job Offer']:
                return <JobOfferStep {...commonProps} />;
            case VisaApplicationStepName['Relatives Abroad']:
                return <RelativesAbroadStep {...commonProps} />;
            case VisaApplicationStepName['Business Ownership']:
                return <BusinessOwnershipStep {...commonProps} onSubmit={handleSubmit} />;
            default:
                return null;
        }
    };

    if (purposeLoading) {
        return (
            <Loader rootSx={{ height: '100dvh' }} message={translate('pleaseWaitLoadingRecords')} />
        );
    }

    return (
        <PageBackground rootSx={{ overflow: 'visible' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            Eligibility Assessment
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {isApplicationComplete
                                ? 'All sections completed'
                                : 'Complete all sections to submit your application'}
                        </Typography>
                    </Box>
                    <Chip
                        label={`${completedCount}/${visaProcessSteps.length}`}
                        color="primary"
                        size="small"
                    />
                </Box>

                {/* Overall Progress */}
                <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            {translate('formProgress')}
                        </Typography>
                        <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                            {Math.round(fieldProgress)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={fieldProgress}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                            },
                        }}
                    />
                </Box>
            </Paper>

            <Box
                sx={{
                    display: 'flex',
                    gap: 3,
                    flexDirection: { xs: 'column', md: 'row' },
                    position: 'relative',
                }}
            >
                {/* Accordion Steps */}
                <Box sx={{ flex: 1 }}>
                    {visaProcessSteps.map((step, index: number) => {
                        const formKey = CompleteFormNameMapping[step];
                        const isCompleted =
                            completedSteps.has(formKey) || Boolean(completedForms?.[formKey]);
                        const isActive = expanded === step;

                        return (
                            <Accordion
                                id={`visa-accordion-${step}`}
                                key={step}
                                expanded={expanded === step}
                                onChange={handleAccordionChange(step)}
                                sx={{
                                    mb: 2,
                                    borderRadius: '12px !important',
                                    border: '1px solid',
                                    borderColor: isActive ? 'primary.main' : 'divider',
                                    boxShadow: isActive
                                        ? '0 4px 12px rgba(102, 126, 234, 0.15)'
                                        : 'none',
                                    '&:before': { display: 'none' },
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMore />}
                                    sx={{
                                        minHeight: { xs: 64, sm: 72 },
                                        '& .MuiAccordionSummary-content': {
                                            my: { xs: 1.5, sm: 2 },
                                            alignItems: 'center',
                                        },
                                        bgcolor: isActive
                                            ? 'rgba(102, 126, 234, 0.04)'
                                            : 'transparent',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            width: '100%',
                                        }}
                                    >
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    fontSize: { xs: '0.938rem', sm: '1rem' },
                                                    fontWeight: isActive ? 600 : 500,
                                                }}
                                            >
                                                {translate(stepLabelKey[step] ?? step)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {translate('assessmentStepProgress')
                                                    .replace('{{current}}', String(index + 1))
                                                    .replace(
                                                        '{{total}}',
                                                        String(visaProcessSteps.length)
                                                    )}
                                            </Typography>
                                        </Box>
                                        {isCompleted ? (
                                            <CheckCircle color="success" sx={{ fontSize: 24 }} />
                                        ) : isActive ? (
                                            <Edit color="primary" sx={{ fontSize: 22 }} />
                                        ) : (
                                            <RadioButtonUnchecked
                                                sx={{ fontSize: 22, color: 'grey.400' }}
                                            />
                                        )}
                                        {!isDesktop && (
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setMobileHelpOpen((prev) => ({
                                                        ...prev,
                                                        [step]: !prev[step],
                                                    }));
                                                }}
                                                sx={{ ml: 1 }}
                                            >
                                                <Info />
                                            </IconButton>
                                        )}
                                    </Box>
                                </AccordionSummary>
                                {expanded === step ? (
                                    <AccordionDetails
                                        sx={{ pt: 2, pb: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}
                                    >
                                        {/* show help in mobile here which should be toggleable */}
                                        {!isDesktop &&
                                            mobileHelpOpen[step] &&
                                            (formStepFieldDescriptions[step].length &&
                                            formStepFieldDescriptions[step] ? (
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        mb: 2,
                                                        p: 2,
                                                        bgcolor: 'grey.50',
                                                        borderRadius: 1,
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                    }}
                                                >
                                                    <Typography variant="h6" gutterBottom>
                                                        {translate('fieldInformation')}
                                                    </Typography>
                                                    <List dense>
                                                        {formStepFieldDescriptions[step].map(
                                                            (item, idx) => (
                                                                <ListItem key={idx} sx={{ px: 0 }}>
                                                                    <ListItemText
                                                                        primary={
                                                                            <Typography
                                                                                variant="subtitle2"
                                                                                sx={{
                                                                                    fontWeight: 600,
                                                                                }}
                                                                            >
                                                                                {item.field}
                                                                            </Typography>
                                                                        }
                                                                        secondary={translate(
                                                                            item.explanation
                                                                        )}
                                                                    />
                                                                </ListItem>
                                                            )
                                                        )}
                                                    </List>
                                                </Paper>
                                            ) : null)}

                                        {renderStep(step)}
                                    </AccordionDetails>
                                ) : null}
                            </Accordion>
                        );
                    })}
                </Box>

                <Box
                    sx={{
                        flex: '0 0 400px',
                        position: 'sticky',
                        top: 20,
                        alignSelf: 'flex-start',
                        display: { xs: 'none', md: 'block' },
                        maxHeight: 'calc(100dvh - 40px)',
                        overflowY: 'auto',
                        boxSizing: 'border-box',
                    }}
                >
                    {formStepFieldDescriptions[expanded]?.length ? (
                        <Paper
                            elevation={0}
                            sx={{
                                mb: 0,
                                p: 2,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                {translate('fieldInformation')}
                            </Typography>
                            <List dense>
                                {formStepFieldDescriptions[expanded].map((item, idx) => (
                                    <ListItem key={idx} sx={{ px: 0, py: 1 }}>
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    {translate(item.field)}
                                                </Typography>
                                            }
                                            secondary={translate(item.explanation)}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            {translate('noHelpForSection')}
                        </Typography>
                    )}
                </Box>
            </Box>
        </PageBackground>
    );
};

export default VisaApplication;
